// Helper de formatação monetária "centavos sempre", usado pela tela única de
// assinatura (grid desktop + carrossel mobile) e pelo histórico de compras.
// Diferente do padrão antigo (que escondia os centavos quando eram 00), aqui o
// valor SEMPRE exibe os centavos — ex.: 199 → "199,00"; 199.9 → "199,90".
//
// `formatMoney` não inclui prefixo de moeda e formata em pt-br por padrão, para
// os componentes de plano que já o usam assim. Quem tem a moeda em mãos — o
// histórico, onde ela vem do backend por pedido — usa `formatCurrency`.
//
// Implementação manual de propósito: `Intl.NumberFormat` diverge entre Node e
// browser conforme o ICU disponível, e este helper roda em telas SSR — a
// divergência apareceria como mismatch de hidratação.

export type MoneyLocale = "pt-br" | "en";
export type CurrencyCode = "BRL" | "USD";

const SEPARATORS: Record<MoneyLocale, { thousands: string; decimal: string }> = {
  "pt-br": { thousands: ".", decimal: "," },
  en: { thousands: ",", decimal: "." },
};

const SYMBOLS: Record<CurrencyCode, string> = {
  BRL: "R$",
  USD: "US$",
};

export interface FormatMoneyOptions {
  /** Quando true, sempre mostra os dois dígitos de centavos (inclusive ",00"). */
  alwaysCents?: boolean;
  /** Separadores a usar. Default "pt-br", que preserva o comportamento antigo. */
  locale?: MoneyLocale;
}

/**
 * Coage um valor (number | string com vírgula ou ponto) para número.
 * Aceita "199,90", "199.90", "1.234,56", "1,234.56", 199.9. Retorna 0 quando inválido.
 */
function toNumber(value: unknown): number {
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  if (value == null) return 0;

  const raw = String(value).replace(/\s/g, "");
  const lastComma = raw.lastIndexOf(",");
  const lastDot = raw.lastIndexOf(".");

  let normalized: string;

  if (lastComma >= 0 && lastDot >= 0) {
    // Tem os dois: o que vem por último é o decimal, o outro é milhar.
    const decimalAt = Math.max(lastComma, lastDot);
    normalized =
      raw.slice(0, decimalAt).replace(/[.,]/g, "") + "." + raw.slice(decimalAt + 1);
  } else if (lastComma >= 0 || lastDot >= 0) {
    const at = Math.max(lastComma, lastDot);
    const separator = raw[at];
    const groups = raw.split(separator).length - 1;
    const afterLast = raw.slice(at + 1);

    // Separador repetido, ou seguido de exatamente 3 dígitos, é milhar:
    // "1.234.567" e "1,234" são 1234567 e 1234. Sem isto, "1,234.56" — o formato
    // en-US que este próprio helper produz — viraria NaN → 0, silenciosamente.
    normalized =
      groups > 1 || /^\d{3}$/.test(afterLast)
        ? raw.replace(/[.,]/g, "")
        : raw.slice(0, at) + "." + afterLast;
  } else {
    normalized = raw;
  }

  const n = Number(normalized);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Formata um valor monetário com centavos sempre presentes quando `alwaysCents`
 * é true (padrão da tela única). Não adiciona prefixo de moeda.
 */
export function formatMoney(
  value: unknown,
  options: FormatMoneyOptions = {}
): string {
  const { alwaysCents = true, locale = "pt-br" } = options;
  const { thousands, decimal } = SEPARATORS[locale] || SEPARATORS["pt-br"];
  const num = toNumber(value);

  const fixed = num.toFixed(2); // "199.00"
  const [intPart, decPart] = fixed.split(".");

  const intWithThousands = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);

  if (alwaysCents) {
    return `${intWithThousands}${decimal}${decPart}`;
  }

  // Sem centavos quando forem "00".
  return decPart === "00"
    ? intWithThousands
    : `${intWithThousands}${decimal}${decPart}`;
}

/**
 * Valor com o símbolo da moeda em que ele está — "US$ 1,234.56", "R$ 1.234,56".
 *
 * A MOEDA vem do pedido (o backend a deriva do plano); o LOCALE vem da página.
 * São independentes: um brasileiro navegando em pt-br pode ter um pedido antigo
 * em dólar e deve ler "US$ 1.234,56" — símbolo do dólar, separadores brasileiros.
 *
 * Moeda desconhecida cai em BRL: nunca rotular como dólar um valor que pode ser real.
 */
export function formatCurrency(
  value: unknown,
  currency?: string | null,
  locale: MoneyLocale = "pt-br",
  symbol?: string | null
): string {
  const code: CurrencyCode = currency === "USD" ? "USD" : "BRL";

  return `${symbol || SYMBOLS[code]} ${formatMoney(value, { alwaysCents: true, locale })}`;
}
